// Reusable wrapper for static legal pages (Privacy, Terms, Cookies).
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";

export default function LegalPage({ eyebrow, title, updatedAt, sections, related = [] }) {
  return (
    <SiteLayout>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-dotgrid opacity-60 pointer-events-none" />
        <div className="container-cv relative pt-14 md:pt-20 pb-10">
          <span className="section-eyebrow">{eyebrow}</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-primary leading-[1.04] max-w-4xl">
            {title}
          </h1>
          <p className="mt-4 text-sm text-muted-foreground">
            Last updated: <span className="font-semibold text-primary">{updatedAt}</span>
          </p>
        </div>
      </section>

      <section className="pb-20 md:pb-28">
        <div className="container-cv grid lg:grid-cols-12 gap-10">
          {/* Sticky ToC */}
          <aside className="lg:col-span-3">
            <div className="sticky top-28 rounded-xl border border-border bg-white p-4">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Contents</div>
              <ol className="mt-3 space-y-2">
                {sections.map((s, i) => (
                  <li key={i}>
                    <a
                      href={`#${s.id}`}
                      className="text-sm text-primary/80 hover:text-accent transition-colors leading-snug"
                    >
                      <span className="font-mono text-[10px] text-muted-foreground mr-2">{String(i + 1).padStart(2, "0")}</span>
                      {s.heading}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Body */}
          <article className="lg:col-span-9 max-w-3xl">
            {sections.map((s, i) => (
              <section key={i} id={s.id} className="scroll-mt-28 mb-12">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-primary tracking-tight">
                  <span className="font-mono text-sm text-accent mr-2">{String(i + 1).padStart(2, "0")}</span>
                  {s.heading}
                </h2>
                <div className="mt-4 space-y-4">
                  {s.body.map((blk, j) => {
                    if (blk.type === "p") {
                      return (
                        <p key={j} className="text-base text-muted-foreground leading-relaxed">{blk.text}</p>
                      );
                    }
                    if (blk.type === "list") {
                      return (
                        <ul key={j} className="list-disc pl-6 space-y-2 text-base text-muted-foreground">
                          {blk.items.map((it, k) => (
                            <li key={k} className="leading-relaxed">{it}</li>
                          ))}
                        </ul>
                      );
                    }
                    if (blk.type === "h3") {
                      return (
                        <h3 key={j} className="mt-6 font-display text-lg font-bold text-primary">{blk.text}</h3>
                      );
                    }
                    if (blk.type === "callout") {
                      return (
                        <div key={j} className="rounded-xl border border-accent/40 bg-accent/10 p-4 text-sm text-primary">{blk.text}</div>
                      );
                    }
                    return null;
                  })}
                </div>
              </section>
            ))}

            {related.length > 0 && (
              <div className="mt-12 pt-8 border-t border-border">
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Related</div>
                <div className="mt-3 flex flex-wrap gap-3">
                  {related.map((r, i) => (
                    <Link key={i} to={r.href} className="inline-flex items-center gap-1 text-sm font-bold text-primary hover:text-accent transition-colors">
                      {r.label} <ArrowRight className="h-3.5 w-3.5" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </article>
        </div>
      </section>
    </SiteLayout>
  );
}
